import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR, { useSWRConfig } from "swr";
import { toast } from "react-toastify";
import { LayoutTemplate } from "lucide-react";
import { type WorkflowTemplate } from "~/api/types";
import { simpleFetcher } from "~/api/fetcher";
import { mutateDocumentReleases } from "~/api/v2/useDocumentReleases";
import { useWorkflowTemplates } from "~/api/v2/useWorkflowTemplates";
import {
  createWorkflowTemplate,
  duplicateWorkflowTemplate,
  updateWorkflowTemplate,
  deleteWorkflowTemplates,
} from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "~/atoms/empty";
import { Skeleton } from "~/atoms/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/atoms/tabs";
import { useTemplateEditor } from "~/hooks/useTemplateEditor";
import { useUnsavedChangesGuard } from "~/hooks/useUnsavedChangesGuard";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import HeadingPage from "~/molecules/HeadingPage";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import {
  WorkflowTemplateSidebar,
  WorkflowTemplateSidebarSkeleton,
  type SidebarTemplate,
} from "~/molecules/WorkflowTemplateSidebar";
import { TemplateApplyTab } from "~/organisms/workflow-template/TemplateApplyTab";
import {
  WorkflowTemplateEditor,
  WorkflowTemplateEditorSkeleton,
} from "~/organisms/workflow-template/WorkflowTemplateEditor";

/** Sentinel ID used for a new, unsaved template */
const NEW_TEMPLATE_ID = -1;

export default function WorkflowTemplatesPage() {
  const { t } = useTranslation();
  const { mutate: mutateCache } = useSWRConfig();

  const {
    data: templates,
    isLoading: templatesLoading,
    mutate: mutateTemplates,
  } = useWorkflowTemplates();

  const editor = useTemplateEditor();

  const [selectedTemplateIdState, setSelectedTemplateId] = useState<
    number | null
  >();
  // The default template is sorted to the top of the sidebar. Undefined means
  // an initial selection has not been made; null means the user closed it.
  const firstTemplate =
    templates?.find((template) => template.isDefault) ?? templates?.[0];
  const selectedTemplateId =
    selectedTemplateIdState === undefined
      ? (firstTemplate?.id ?? null)
      : selectedTemplateIdState;
  /** The sentinel id is only ever selected while an unsaved template is open. */
  const isNewTemplate = selectedTemplateId === NEW_TEMPLATE_ID;

  const { data: selectedTemplateDetail, mutate: mutateDetail } =
    useSWR<WorkflowTemplate>(
      selectedTemplateId !== null && selectedTemplateId !== NEW_TEMPLATE_ID
        ? `/api/v2/workflow-templates/${selectedTemplateId}`
        : null,
      simpleFetcher,
    );

  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<
    number | null
  >(null);
  const deleteTarget =
    deleteTargetId === NEW_TEMPLATE_ID
      ? {
          name: editor.editedName || t(($) => $.workflowTemplates.newTemplate),
        }
      : templates?.find((tmpl) => tmpl.id === deleteTargetId);

  const { guard, alertProps } = useUnsavedChangesGuard(
    editor.hasUnsavedChanges || isNewTemplate,
  );

  // The unsaved draft is shown alongside the saved templates until it is saved.
  const sidebarTemplates: SidebarTemplate[] = isNewTemplate
    ? [
        ...(templates ?? []),
        {
          id: NEW_TEMPLATE_ID,
          name: editor.editedName || t(($) => $.workflowTemplates.newTemplate),
          isDefault: false,
          isDraft: true,
        },
      ]
    : (templates ?? []);

  const hasTemplateOpen =
    selectedTemplateId !== null && (isNewTemplate || !!selectedTemplateDetail);

  const selectTemplate = (id: number) => {
    if (id === NEW_TEMPLATE_ID) return;

    // Selecting the open template again closes it.
    if (id === selectedTemplateId && !isNewTemplate) {
      setSelectedTemplateId(null);
      editor.clearLoadedTemplate();
      editor.setHasUnsavedChanges(false);
      return;
    }

    setSelectedTemplateId(id);
    editor.setHasUnsavedChanges(false);
  };

  useEffect(() => {
    if (selectedTemplateId === selectedTemplateDetail?.id) {
      editor.syncToTemplate(selectedTemplateDetail);
    }
  }, [editor, selectedTemplateDetail, selectedTemplateId]);

  const createTemplate = () => {
    setSelectedTemplateId(NEW_TEMPLATE_ID);
    editor.resetForNewTemplate();
  };

  const handleDelete = (id: number) => {
    const isDefault =
      templates?.find((tmpl) => tmpl.id === id)?.isDefault ?? false;
    if (isDefault) {
      toast.error(t(($) => $.workflowTemplates.cannotDeleteDefault));
      return;
    }
    setDeleteTargetId(id);
  };

  const handleDuplicate = async (id: number) => {
    const source = templates?.find((template) => template.id === id);
    if (!source) return;

    const sourceName =
      source.name ?? t(($) => $.workflowTemplates.defaultBadge);
    const duplicateName = t(($) => $.workflowTemplates.duplicateName, {
      name: sourceName,
    }).slice(0, 255);

    setDuplicatingTemplateId(id);
    try {
      const duplicatedTemplate = await duplicateWorkflowTemplate(
        id,
        duplicateName,
      );

      await Promise.all([
        mutateTemplates(),
        mutateCache(
          `/api/v2/workflow-templates/${duplicatedTemplate.id}`,
          duplicatedTemplate,
          { revalidate: false },
        ),
      ]);

      editor.loadFromTemplate(duplicatedTemplate);
      setSelectedTemplateId(duplicatedTemplate.id);
      toast.success(t(($) => $.workflowTemplates.duplicateSuccess));
    } catch {
      toast.error(t(($) => $.workflowTemplates.duplicateError));
    } finally {
      setDuplicatingTemplateId(null);
    }
  };

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;

    if (deleteTargetId !== NEW_TEMPLATE_ID) {
      try {
        await deleteWorkflowTemplates([deleteTargetId]);
        await mutateTemplates();
        toast.success(t(($) => $.workflowTemplates.deleteSuccess));
      } catch {
        toast.error(t(($) => $.workflowTemplates.deleteError));
        setDeleteTargetId(null);
        return;
      }
    }

    if (selectedTemplateId === deleteTargetId) {
      setSelectedTemplateId(null);
      editor.clearLoadedTemplate();
      // Its edits went with it, so the next sidebar click must not be met with
      // an unsaved-changes prompt for a template that no longer exists.
      editor.setHasUnsavedChanges(false);
    }

    setDeleteTargetId(null);
  };

  const handleSaveTemplate = async () => {
    if (selectedTemplateId === null) return;

    const trimmedName = editor.editedName.trim();
    const isDefault = selectedTemplateDetail?.isDefault ?? false;
    if (!trimmedName && !isDefault) {
      toast.error(t(($) => $.workflowTemplates.templateNameRequired));
      return;
    }

    const invalidStep = editor.editedSteps.find((step) => !step.name.trim());
    if (invalidStep) {
      toast.error(t(($) => $.commons.workflow.allStepsMustHaveName));
      return;
    }

    editor.setIsSaving(true);

    try {
      const payload = {
        name: trimmedName || null,
        description: editor.editedDescription,
        steps: editor.editedSteps.map((step) => ({
          id: step.id,
          name: step.name,
          description: step.description || null,
          type: step.type,
          dueAt: step.dueAt,
          users: step.users.map((u) => ({
            userId: u.userId,
            permission: u.permission,
          })),
        })),
      };

      let savedTemplate: WorkflowTemplate;

      if (isNewTemplate) {
        savedTemplate = await createWorkflowTemplate(payload);
        setSelectedTemplateId(savedTemplate.id);
      } else {
        savedTemplate = await updateWorkflowTemplate(
          selectedTemplateId,
          payload,
        );
      }

      editor.loadFromTemplate(savedTemplate);

      await Promise.all([
        mutateTemplates(),
        mutateDocumentReleases(),
        mutateDetail(savedTemplate, { revalidate: false }),
      ]);

      toast.success(t(($) => $.workflowTemplates.saveSuccess));
    } catch {
      toast.error(t(($) => $.workflowTemplates.saveError));
    } finally {
      editor.setIsSaving(false);
    }
  };

  const isDetailsLoading =
    selectedTemplateId !== null && !isNewTemplate && !selectedTemplateDetail;

  const editorActions = (
    <>
      {!(selectedTemplateDetail?.isDefault ?? false) && (
        <Button
          variant="destructive"
          onClick={() => handleDelete(selectedTemplateId ?? NEW_TEMPLATE_ID)}
        >
          {t(($) => $.workflowTemplates.deleteTemplate)}
        </Button>
      )}
      <Button
        onClick={handleSaveTemplate}
        disabled={
          editor.isSaving || (!isNewTemplate && !editor.hasUnsavedChanges)
        }
      >
        {editor.isSaving
          ? t(($) => $.commons.buttons.saving)
          : t(($) => $.workflowTemplates.saveTemplate)}
      </Button>
    </>
  );

  return (
    <div className="container flex grow flex-col gap-6 py-7">
      <HeadingPage title={t(($) => $.workflowTemplates.title)} />

      <div
        className="flex min-h-150 gap-6"
        inert={duplicatingTemplateId !== null}
      >
        <div className="sticky top-6 h-[calc(100dvh-11.5rem)] w-75 shrink-0 self-start">
          {templatesLoading ? (
            <WorkflowTemplateSidebarSkeleton />
          ) : (
            <WorkflowTemplateSidebar
              templates={sidebarTemplates}
              selectedTemplateId={selectedTemplateId}
              duplicatingTemplateId={duplicatingTemplateId}
              onSelect={(id) => guard(() => selectTemplate(id))}
              onCreate={() => guard(createTemplate)}
              onDuplicate={(id) =>
                guard(() => {
                  void handleDuplicate(id);
                })
              }
              onDelete={handleDelete}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          {hasTemplateOpen ? (
            <Tabs defaultValue="editor">
              <TabsList layout="compact">
                <TabsTrigger value="editor">
                  {t(($) => $.workflowTemplates.tabs.editor)}
                </TabsTrigger>
                <TabsTrigger value="apply">
                  {t(($) => $.workflowTemplates.tabs.applyToDocuments)}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="editor"
                forceMount
                className="data-[state=inactive]:hidden"
              >
                <WorkflowTemplateEditor
                  editedSteps={editor.editedSteps}
                  setEditedSteps={editor.setEditedSteps}
                  editedName={editor.editedName}
                  onNameChange={editor.editName}
                  editedDescription={editor.editedDescription}
                  onDescriptionChange={editor.editDescription}
                  setHasUnsavedChanges={editor.setHasUnsavedChanges}
                  actions={editorActions}
                />
              </TabsContent>

              <TabsContent
                value="apply"
                forceMount
                className="data-[state=inactive]:hidden"
              >
                {/* Keyed on the template so switching one clears the
                    document selection and any half-finished apply. */}
                <TemplateApplyTab
                  key={selectedTemplateId}
                  template={
                    isNewTemplate ? null : (selectedTemplateDetail ?? null)
                  }
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="animate-in fade-in duration-200">
              {isDetailsLoading || templatesLoading ? (
                <div className="w-full">
                  <div className="flex gap-2">
                    <Skeleton className="h-9 w-24 rounded-md" />
                    <Skeleton className="h-9 w-36 rounded-md" />
                  </div>
                  <WorkflowTemplateEditorSkeleton />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <Empty className="h-fit border-0">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <LayoutTemplate />
                      </EmptyMedia>
                      <EmptyDescription>
                        {(templates?.length ?? 0) === 0 && !isNewTemplate
                          ? t(($) => $.workflowTemplates.noTemplates)
                          : t(($) => $.workflowTemplates.noTemplateSelected)}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetId(null);
        }}
        title={t(($) => $.workflowTemplates.deleteConfirm.title)}
        description={t(($) => $.workflowTemplates.deleteConfirm.description, {
          name: deleteTarget?.name ?? "",
        })}
        confirmLabel={t(($) => $.commons.buttons.delete)}
        onConfirm={confirmDelete}
      />
      <UnsavedChangesAlert {...alertProps} ignoreSearchChanges />
    </div>
  );
}
